// ═══════════════════════════════════════════════════════════════════════════
// commandDispatch/blockAuthoringCommands.ts — TESTBLOCK / CLOSETESTBLOCK /
// BAUTHORPALETTE(+CLOSE) / BPARAMETER / BACTION / BVSTATE / BVHIDE / BVSHOW /
// BVSHOWALL / BCPARAMETER / BGRIPSET / BLOOKUPTABLE / BTABLE, extracted
// verbatim from App.tsx's `submitCommandText` (D5).
// ═══════════════════════════════════════════════════════════════════════════

import type { DispatchContext, DispatchResult } from "./types";

/**
 * BPARAMETER / BACTION / BCPARAMETER / BGRIPSET all require an active BEDIT
 * session before doing anything else; each shared the same 3-line "look up
 * the active space's kind, bail with a toast if it isn't block-edit" guard
 * (jscpd flagged 4 copies in this file). One truth here.
 */
function isBlockEditActive(geometryDocument: DispatchContext["geometryDocument"]): boolean {
  const activeKind = geometryDocument.spaces.find(
    (s) => s.id === geometryDocument.activeSpaceId,
  )?.kind;
  return activeKind === "block-edit";
}

/**
 * DYN-1.9 TESTBLOCK / CLOSETESTBLOCK — TESTBLOCK is valid only inside
 * BEDIT: we test the block being authored. CLOSETESTBLOCK is valid only
 * inside the test-block sandbox. Both dispatch directly — there is no
 * drafting state machine step because the preview is a space activation,
 * not a multi-click authoring flow. Also covers every other
 * BEDIT-scoped block-authoring command: BAUTHORPALETTE(+CLOSE) /
 * BPARAMETER / BACTION / BVSTATE / BVHIDE / BVSHOW / BVSHOWALL /
 * BCPARAMETER / BGRIPSET / BLOOKUPTABLE / BTABLE.
 */
export function handleBlockAuthoringCommands(
  ctx: DispatchContext,
): DispatchResult {
  const {
    draftSession,
    resolvedCommandId,
    state,
    dispatch,
    geometryDocument,
    appendCommandFeed,
    t,
    setCommandText,
    blockAuthoringShell,
    setLookupTableDialog,
    setBlockPropertiesDialogOpen,
  } = ctx;

  if (draftSession) return null;

  if (resolvedCommandId === "TESTBLOCK") {
    const activeKind = geometryDocument.spaces.find(
      (s) => s.id === geometryDocument.activeSpaceId,
    )?.kind;
    if (activeKind !== "block-edit") {
      appendCommandFeed(t("toast.testblock.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    const editing = geometryDocument.spaces.find((s) => s.kind === "block-edit");
    const blockName = editing?.blockEditName;
    if (!blockName) {
      appendCommandFeed(t("toast.testblock.no_block"));
      setCommandText("");
      return true;
    }
    dispatch({
      type: "space/enter-block-test",
      source: "commandline",
      blockName,
    });
    appendCommandFeed(t("summary.testblock_entered", { name: blockName }));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "CLOSETESTBLOCK") {
    const activeKind = geometryDocument.spaces.find(
      (s) => s.id === geometryDocument.activeSpaceId,
    )?.kind;
    if (activeKind !== "block-test") {
      appendCommandFeed(t("toast.testblock.not_in_test_block"));
      setCommandText("");
      return true;
    }
    dispatch({
      type: "space/exit-block-test",
      source: "commandline",
    });
    appendCommandFeed(t("summary.testblock_exited"));
    setCommandText("");
    return true;
  }
  // ── DYN-1.11 Block Authoring Palette commands ───────────────
  // BAUTHORPALETTE / BAUTHORPALETTECLOSE toggle the palette's
  // visibility within a BEDIT session. The palette itself only
  // mounts when active space is "block-edit"; the flag just hides
  // / shows it inside that session. Outside BEDIT the commands
  // are a no-op (AutoCAD also silently ignores them there).
  if (resolvedCommandId === "BAUTHORPALETTE") {
    blockAuthoringShell.palette.toggle();
    appendCommandFeed(t("summary.bauthorpalette_toggled"));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "BAUTHORPALETTECLOSE") {
    blockAuthoringShell.palette.close();
    appendCommandFeed(t("summary.bauthorpalette_closed"));
    setCommandText("");
    return true;
  }
  // BPARAMETER / BACTION / BVSTATE require a BEDIT session and
  // route the command to the Authoring Palette via
  // `blockAuthoringShell.palette.requestOpen()`. The palette
  // consumes the request in an effect, switches to the right tab,
  // or opens the Visibility States dialog on the first visibility
  // parameter.
  if (resolvedCommandId === "BPARAMETER") {
    if (!isBlockEditActive(geometryDocument)) {
      appendCommandFeed(t("toast.authoring.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    blockAuthoringShell.palette.requestOpen("parameter");
    appendCommandFeed(t("prompt.bparameter.select_kind"));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "BACTION") {
    if (!isBlockEditActive(geometryDocument)) {
      appendCommandFeed(t("toast.authoring.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    blockAuthoringShell.palette.requestOpen("action");
    appendCommandFeed(t("prompt.baction.select_parameter"));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "BVSTATE") {
    if (!isBlockEditActive(geometryDocument)) {
      appendCommandFeed(t("toast.authoring.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    blockAuthoringShell.palette.requestOpen("visibility");
    setCommandText("");
    return true;
  }
  // Phase K.4 — BVHIDE / BVSHOW [_All] command-line entry points.
  // Operate on the active selection in BEDIT against the visibility
  // parameter's current state (BVSHOW _All ignores currentStateId
  // and updates every state). All three are no-ops when:
  //   - not in BEDIT (toast)
  //   - the block has no visibility parameter (toast — same as BVSTATE)
  //   - the user has nothing selected (toast)
  if (
    resolvedCommandId === "BVHIDE" ||
    resolvedCommandId === "BVSHOW" ||
    resolvedCommandId === "BVSHOWALL"
  ) {
    const activeGeoSpace = geometryDocument.spaces.find(
      (s) => s.id === geometryDocument.activeSpaceId,
    );
    if (activeGeoSpace?.kind !== "block-edit" || !activeGeoSpace.blockEditName) {
      appendCommandFeed(t("toast.authoring.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    const blockName = activeGeoSpace.blockEditName;
    const blockDef = geometryDocument.blocks[blockName];
    const visParam = blockDef?.dynamicMetadata?.parameters.find(
      (p) => p.kind === "visibility",
    );
    if (!visParam || visParam.kind !== "visibility") {
      appendCommandFeed(t("toast.authoring.no_visibility_param"));
      setCommandText("");
      return true;
    }
    const ids = state.selection.entityIds;
    if (ids.length === 0) {
      // AutoCAD also gates on selection — nothing selected → no-op
      // with a hint. Re-use the prompt text from BVSTATE flow.
      appendCommandFeed(t("toast.authoring.no_visibility_param"));
      setCommandText("");
      return true;
    }
    if (resolvedCommandId === "BVHIDE") {
      dispatch({
        type: "geometry/bvhide",
        source: "commandline",
        blockName,
        parameterId: visParam.id,
        stateId: visParam.currentStateId,
        entityIds: [...ids],
      });
    } else if (resolvedCommandId === "BVSHOWALL") {
      dispatch({
        type: "geometry/bvshow-all",
        source: "commandline",
        blockName,
        parameterId: visParam.id,
        entityIds: [...ids],
      });
    } else {
      dispatch({
        type: "geometry/bvshow",
        source: "commandline",
        blockName,
        parameterId: visParam.id,
        stateId: visParam.currentStateId,
        entityIds: [...ids],
      });
    }
    setCommandText("");
    return true;
  }
  // DYN-2 Batch C — BCPARAMETER opens the Constraints tab in the
  // block authoring palette. AutoCAD's BCPARAMETER is closer to a
  // "convert dimensional constraint to parameter" wizard, but in
  // Tier 2 our constraint parameters are created directly via the
  // palette UI, so the command is reduced to a tab-switch trigger.
  if (resolvedCommandId === "BCPARAMETER") {
    if (!isBlockEditActive(geometryDocument)) {
      appendCommandFeed(t("toast.authoring.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    blockAuthoringShell.palette.requestOpen("constraint");
    appendCommandFeed(t("prompt.bcparameter.open"));
    setCommandText("");
    return true;
  }
  // BGRIPSET is a thin wrapper that surfaces the grip-display
  // toggle; Tier 1 has no dedicated dialog yet so for parity we
  // accept the command and log a hint. Full BGRIPSET UI lands in
  // a later tier (tracked in the DYN-1.11 plan).
  if (resolvedCommandId === "BGRIPSET") {
    if (!isBlockEditActive(geometryDocument)) {
      appendCommandFeed(t("toast.authoring.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    appendCommandFeed(t("feed.bgripset.tier1_notice"));
    setCommandText("");
    return true;
  }
  // DYN-2 B.2 — BLOOKUPTABLE: open the Lookup Table editor for the
  // active block. If the block already has one lookup table, open it
  // directly; if multiple exist, open the first (a picker UI is a
  // future enhancement); if none, create an empty one and open it.
  if (resolvedCommandId === "BLOOKUPTABLE") {
    const activeSpace = geometryDocument.spaces.find(
      (s) => s.id === geometryDocument.activeSpaceId,
    );
    if (activeSpace?.kind !== "block-edit" || !activeSpace.blockEditName) {
      appendCommandFeed(t("toast.blookuptable.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    const blockName = activeSpace.blockEditName;
    const blockDef = geometryDocument.blocks[blockName];
    const tables = blockDef?.dynamicMetadata?.lookupTables ?? [];
    if (tables.length > 0) {
      const first = tables[0];
      if (first) {
        setLookupTableDialog({ blockName, tableId: first.id });
        appendCommandFeed(
          t("summary.lookup_table_opened", {
            name: first.name || first.id,
          }),
        );
      }
    } else {
      // Mint an empty table, dispatch a create, and open the dialog
      // on it. The dialog preloads with the newly minted id.
      const usedIds = new Set(tables.map((tt) => tt.id));
      let i = 1;
      while (usedIds.has(`lookup_table-${i}`)) i += 1;
      const newId = `lookup_table-${i}`;
      const usedNames = new Set(tables.map((tt) => tt.name));
      let j = 1;
      while (usedNames.has(`Table${j}`)) j += 1;
      const newName = `Table${j}`;
      dispatch({
        type: "geometry/create-lookup-table",
        source: "commandline",
        blockName,
        table: {
          id: newId,
          name: newName,
          inputColumns: [],
          rows: [],
        },
      });
      setLookupTableDialog({ blockName, tableId: newId });
      appendCommandFeed(t("summary.lookup_table_created", { name: newName }));
    }
    setCommandText("");
    return true;
  }
  // DYN-2 B.2 — BTABLE: open the Block Properties Table editor.
  if (resolvedCommandId === "BTABLE") {
    const activeSpace = geometryDocument.spaces.find(
      (s) => s.id === geometryDocument.activeSpaceId,
    );
    if (activeSpace?.kind !== "block-edit" || !activeSpace.blockEditName) {
      appendCommandFeed(t("toast.btable.not_in_block_editor"));
      setCommandText("");
      return true;
    }
    setBlockPropertiesDialogOpen(true);
    appendCommandFeed(t("prompt.btable.opened"));
    setCommandText("");
    return true;
  }

  return null;
}
